import * as React from 'react';
import { connect } from 'react-redux';
import { actions } from 'react-redux-form';
import * as validator from 'validator';
import { merge } from 'lodash';
import * as deepEqual from 'deep-equal';
import { Button, Card, Form, Notification } from '..';
import { addDriver, showError, clearErrors } from '../../redux/actions/drivers';
import FIELDS from '../../data/fields';
import REPUTATIONS from '../../data/reputations';
import AddressPicker from '../AddressPicker/AddressPicker';
import CompanyPicker from '../Companies/CompanyPicker';

interface Props {
  handleUpdate: Function;
  isDisabled?: boolean;
  isLoading: boolean;
  didInvalidate: boolean;
  errorMessage: string | boolean;
  dispatch: Function;
  createButtonText?: string;
  isSectioned?: boolean;
  onUpdate: Function;
  onErrors?: (errors) => void;
}

interface State {
  formData: any;
  initialFormData: any;
  section: number;
  errors: string[];
}

class AdditionalDriverCreateForm extends React.Component<Props, State> {
  private readyCount: number;

  constructor(props) {
    super(props);
    this.state = {
      formData: {},
      initialFormData: null,
      section: 0,
      errors: [],
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.checkForReady = this.checkForReady.bind(this);
    this.isChanged = this.isChanged.bind(this);

    // This is used to detect if the form has been initialised
    this.readyCount = 0;
  }

  /**
   * Check if all the forms are ready. If they are store the
   * form data as the initial state.
   */
  checkForReady() {
    if (this.state.initialFormData !== null) {
      return;
    }
    this.readyCount++;
    // Once all the forms have been initialised the initial formData can be stored to
    // check for changes
    if (this.readyCount === 5) {
      this.setState({ initialFormData: merge({}, this.state.formData) });
    }
  }

  /**
   * Return whether the form has changed
   */
  isChanged() {
    return !deepEqual(this.state.formData, this.state.initialFormData);
  }

  /**
   * Handler for storing form data changes
   * @param {Object} data
   */
  handleChange(data) {
    this.setState(
      {
        formData: merge(this.state.formData, data),
      },
      this.checkForReady,
    );
  }

  /**
   * Handling for data validation before submission
   * @param {Object} data
   */
  handleValidate(data) {
    if (this.props.didInvalidate) {
      this.props.dispatch(clearErrors());
    }
    if (this.props.onErrors) {
      this.props.onErrors([]);
    }

    const errorFields = [];
    const errorMessages = [];

    if (
      validator.isEmpty(data.first_name) ||
      validator.isEmpty(data.last_name)
    ) {
      errorFields.push('first_name');
      errorFields.push('last_name');
      errorMessages.push("Please specify the driver's full name");
    }
    if (validator.isEmpty(String(data.phone_number))) {
      errorFields.push('phone_number');
      errorMessages.push('Please specify a phone number');
    }
    if (!validator.isEmpty(data.email) && !validator.isEmail(data.email)) {
      errorFields.push('email');
      errorMessages.push('Please specify a valid email address');
    }
    if (validator.isEmpty(data.occupation)) {
      errorFields.push('occupation');
      errorMessages.push('Please specify an occupation');
    }

    if (validator.isEmpty(data.postcode)) {
      errorFields.push('postcode');
      errorMessages.push('Please specify a post code');
    }

    if (validator.isEmpty(data.address)) {
      errorFields.push('address');
      errorMessages.push('Please specify the address');
    }
    if (validator.isEmpty(data.driving_license_number)) {
      errorFields.push('driving_license_number');
      errorMessages.push('Please specify a driving licence number');
    }
    if (
      !validator.isEmpty(data.driving_license_number) &&
      !validator.isLength(data.driving_license_number, { min: 16, max: 16 })
    ) {
      errorFields.push('driving_license_number');
      errorMessages.push(
        'Please make sure the driving licence number is 16 characters long',
      );
    }

    if (
      !validator.isEmpty(data.expiry_date) &&
      validator.isBefore(data.expiry_date, new Date().toString())
    ) {
      errorFields.push('expiry_date');
      errorMessages.push("Please specify an expiry date that hasn't expired");
    }

    if (errorMessages.length) {
      if (this.props.onErrors) {
        this.props.onErrors(errorMessages);
      } else {
        this.setState({ errors: errorFields });
        this.props.dispatch(showError(errorMessages.join('\n')));
      }
      return false;
    }

    return data;
  }

  /**
   * Handler for form submission
   * @return {Promise}
   */
  async handleSubmit() {
    if (!this.props.isLoading) {
      const data = this.handleValidate(this.state.formData);
      if (data === false) {
        return;
      }
      const isChanged = this.isChanged();
      if (isChanged) {
        const response = await this.props.dispatch(addDriver(data));
        if (response.error) {
          this.props.dispatch(showError(response.error));
          return;
        }
        this.props.onUpdate(response);
      }
      this.props.handleUpdate(isChanged);
    }
  }

  render() {
    const legalDropDownOptions = [
      {
        name: 'Yes',
        value: 1,
      },
      {
        name: 'No',
        value: 0,
      },
    ];
    return (
      <div className={this.props.isSectioned ? 'is-sectioned' : ''}>
        <Card>
          {(!this.props.isSectioned || this.state.section === 0) && (
            <>
              <Form
                fields={FIELDS.drivers.updateGeneric}
                values={{
                  reputation: REPUTATIONS,
                  title: [
                    { name: 'Mr', value: 'Mr' },
                    { name: 'Mrs', value: 'Mrs' },
                    { name: 'Master', value: 'Master' },
                    { name: 'Miss', value: 'Miss' },
                    { name: 'Ms', value: 'Ms' },
                    { name: 'Mx', value: 'Mx' },
                  ],
                }}
                type="generic"
                handleChange={(data) => {
                  this.handleChange(data);
                }}
                errors={this.state.errors}
                handleSubmit={this.handleSubmit}
                getDispatch={(dispatch) => {
                  dispatch(
                    actions.change(
                      'generic.title',
                      this.state.formData.title || '',
                    ),
                  );
                  dispatch(
                    actions.change(
                      'generic.first_name',
                      this.state.formData.first_name || '',
                    ),
                  );
                  dispatch(
                    actions.change(
                      'generic.middle_name',
                      this.state.formData.middle_name || '',
                    ),
                  );
                  dispatch(
                    actions.change(
                      'generic.last_name',
                      this.state.formData.last_name || '',
                    ),
                  );
                  const reputation = this.state.formData.reputation;
                  dispatch(
                    actions.change(
                      'generic.reputation',
                      !isNaN(Number(reputation)) ? reputation : 1,
                    ),
                  );
                  const dob = this.state.formData.date_of_birth;
                  dispatch(
                    actions.change(
                      'generic.date_of_birth',
                      dob ? dob.substring(0, 10) : '',
                    ),
                  );
                }}
                hasButton={false}
                disabled={this.props.isDisabled}
              />
              <AddressPicker
                postcode={this.state.formData.postcode}
                errors={this.state.errors}
                address={{
                  address: this.state.formData.address,
                  address_2: this.state.formData.address_2,
                  city: this.state.formData.city,
                  county: this.state.formData.county,
                }}
                handleUpdate={(
                  postcode,
                  { address, address_2, city, county },
                ) => {
                  this.handleChange({
                    postcode,
                    address,
                    address_2,
                    city,
                    county,
                  });
                }}
              />
            </>
          )}
          {(!this.props.isSectioned || this.state.section === 1) && (
            <>
              <Form
                fields={FIELDS.drivers.updateContact}
                type="contact"
                handleChange={(data) => {
                  this.handleChange(merge(this.state.formData, data));
                }}
                handleSubmit={this.handleSubmit}
                errors={this.state.errors}
                getDispatch={(dispatch) => {
                  dispatch(
                    actions.change(
                      'contact.phone_number',
                      this.state.formData.phone_number || '',
                    ),
                  );
                  dispatch(
                    actions.change(
                      'contact.email',
                      this.state.formData.email || '',
                    ),
                  );
                  dispatch(
                    actions.change(
                      'contact.occupation',
                      this.state.formData.occupation || '',
                    ),
                  );
                }}
                hasButton={false}
                disabled={this.props.isDisabled}
              />
              <CompanyPicker
                companyId={this.state.formData.company_id}
                handleUpdate={(companyId) => {
                  this.handleChange({ company_id: companyId });
                }}
              />
            </>
          )}
          {(!this.props.isSectioned || this.state.section === 2) && (
            <Form
              values={{
                convictions_1: legalDropDownOptions,
                convictions_2: legalDropDownOptions,
                accidents: legalDropDownOptions,
                defects: legalDropDownOptions,
              }}
              fields={FIELDS.drivers.updateLegal}
              type="legal"
              handleChange={(data) => {
                this.handleChange(merge(this.state.formData, data));
              }}
              errors={this.state.errors}
              handleSubmit={this.handleSubmit}
              getDispatch={(dispatch) => {
                dispatch(
                  actions.change(
                    'legal.driving_license_number',
                    this.state.formData.driving_license_number || '',
                  ),
                );
                const expiryDate = this.state.formData.expiry_date;
                dispatch(
                  actions.change(
                    'legal.expiry_date',
                    expiryDate ? expiryDate.substring(0, 10) : '',
                  ),
                );
                const passDate = this.state.formData.pass_date;
                dispatch(
                  actions.change(
                    'legal.pass_date',
                    passDate ? passDate.substring(0, 10) : '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.convictions_1',
                    this.state.formData.convictions_1 || 0,
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.convictions_2',
                    this.state.formData.convictions_2 || 0,
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.convictions_3',
                    this.state.formData.convictions_3 || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.accidents',
                    this.state.formData.accidents || 0,
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.accidents_years',
                    this.state.formData.accidents_years || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.accidents_total_number',
                    this.state.formData.accidents_total_number || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.accidents_total_cost',
                    this.state.formData.accidents_total_cost || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.accidents_outstanding_number',
                    this.state.formData.accidents_outstanding_number || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.accidents_outstanding_cost',
                    this.state.formData.accidents_outstanding_cost || '',
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.defects',
                    this.state.formData.defects || 0,
                  ),
                );
                dispatch(
                  actions.change(
                    'legal.defect_details',
                    this.state.formData.defect_details || '',
                  ),
                );
              }}
              hasButton={false}
              disabled={this.props.isDisabled}
            />
          )}
          <div className="field" />
          {!this.props.isDisabled && (
            <div className="field is-clearfix">
              {(!this.props.isSectioned || this.state.section === 2) && (
                <Button
                  className={`is-primary  is-pulled-right${
                    this.props.isLoading ? ' is-loading' : ''
                  }`}
                  handleClick={this.handleSubmit}
                >
                  {this.props.createButtonText
                    ? this.props.createButtonText
                    : 'Add'}
                </Button>
              )}
            </div>
          )}
        </Card>
        {!this.props.isLoading &&
          this.props.didInvalidate && (
            <Notification type="danger">{this.props.errorMessage}</Notification>
          )}
      </div>
    );
  }
}

const mapStateToProps = ({ drivers }) => ({
  isLoading: drivers.isFetching,
  didInvalidate: drivers.didInvalidate,
  errorMessage: drivers.error,
});

export default connect(mapStateToProps)(AdditionalDriverCreateForm);
